<!-- src/components/ReusePlanPreviewCalendar.vue -->
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { Meal } from '../graphql/types'

dayjs.extend(utc)

interface Props {
  previewMeals: Meal[]
  existingMeals: Meal[]
  mealTypeNamesById?: Record<string, string>
  initialMonth?: string
  selectedDate?: string
}

const props = defineProps<Props>()

const getMealTypeName = (meal: Meal) => props.mealTypeNamesById?.[meal.type_id] || ''
const emit = defineEmits<{
  'select-date': [dateKey: string]
}>()

const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const currentMonth = ref(dayjs(props.initialMonth || undefined).startOf('month'))

// Follow the target date as the user picks/changes it, without losing manual
// navigation the rest of the time (this only fires when the prop itself changes).
watch(
  () => props.initialMonth,
  (month) => {
    if (month) currentMonth.value = dayjs(month).startOf('month')
  },
)

interface DayMeals {
  existing: Meal[]
  preview: Meal[]
}

const mealsByDate = computed(() => {
  const map: Record<string, DayMeals> = {}

  const addMeals = (meals: Meal[], key: keyof DayMeals) => {
    for (const meal of meals) {
      if (!meal.scheduled_date) continue
      const dates = meal.scheduled_dates?.length ? meal.scheduled_dates : [meal.scheduled_date]
      for (const d of dates) {
        const dateKey = dayjs.utc(d).format('YYYY-MM-DD')
        if (!map[dateKey]) map[dateKey] = { existing: [], preview: [] }
        map[dateKey][key].push(meal)
      }
    }
  }

  addMeals(props.existingMeals, 'existing')
  addMeals(props.previewMeals, 'preview')

  return map
})

interface CalendarDay {
  date: dayjs.Dayjs
  utcKey: string
  isCurrentMonth: boolean
  isToday: boolean
  existing: Meal[]
  preview: Meal[]
}

const calendarDays = computed<CalendarDay[]>(() => {
  const start = currentMonth.value.startOf('month')
  const daysInMonth = start.daysInMonth()
  const todayKey = dayjs().format('YYYY-MM-DD')

  const startDow = (start.day() + 6) % 7
  const endDate = start.date(daysInMonth)
  const endDow = (endDate.day() + 6) % 7

  const dayMeals = (utcKey: string): DayMeals => mealsByDate.value[utcKey] || { existing: [], preview: [] }

  const days: CalendarDay[] = []

  for (let i = startDow - 1; i >= 0; i--) {
    const d = start.subtract(i + 1, 'day')
    const utcKey = d.format('YYYY-MM-DD')
    days.push({ date: d, utcKey, isCurrentMonth: false, isToday: false, ...dayMeals(utcKey) })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = start.date(d)
    const utcKey = date.format('YYYY-MM-DD')
    days.push({ date, utcKey, isCurrentMonth: true, isToday: utcKey === todayKey, ...dayMeals(utcKey) })
  }
  for (let i = 1; i <= 6 - endDow; i++) {
    const d = endDate.add(i, 'day')
    const utcKey = d.format('YYYY-MM-DD')
    days.push({ date: d, utcKey, isCurrentMonth: false, isToday: false, ...dayMeals(utcKey) })
  }

  return days
})

const isCurrentMonthNow = computed(() => currentMonth.value.isSame(dayjs().startOf('month')))

// Day-detail popover state — closes whenever the visible month changes.
const openDayKey = ref<string | null>(null)
const popoverStyle = ref({ top: '0px', left: '0px' })
const popoverRef = ref<HTMLElement | null>(null)

const prevMonth = () => { currentMonth.value = currentMonth.value.subtract(1, 'month'); openDayKey.value = null }
const nextMonth = () => { currentMonth.value = currentMonth.value.add(1, 'month'); openDayKey.value = null }
const goToToday = () => { currentMonth.value = dayjs().startOf('month'); openDayKey.value = null }

// Preview meals surface first (they're what the user is here to check), then
// existing ones — capped at 3 per day (up from MealsCalendarView.vue's 2, since
// this card has more room to breathe), with an overflow count for the rest.
const PILL_CAP = 3

const visiblePills = (day: DayMeals) =>
  [
    ...day.preview.map((meal) => ({ meal, isPreview: true as const })),
    ...day.existing.map((meal) => ({ meal, isPreview: false as const })),
  ].slice(0, PILL_CAP)

const overflowCount = (day: DayMeals) => Math.max(0, day.existing.length + day.preview.length - PILL_CAP)

// Full day contents for the "+N More" popover — the pill list is capped, so
// this is how the user sees the rest without relying on mouse hover.
const openDay = computed(() => calendarDays.value.find((d) => d.utcKey === openDayKey.value) || null)
const openDayLabel = computed(() => openDay.value?.date.format('D MMMM YYYY') || '')
const openDayMeals = computed(() => (openDay.value ? visiblePillsAll(openDay.value) : []))

const visiblePillsAll = (day: DayMeals) => [
  ...day.preview.map((meal) => ({ meal, isPreview: true as const })),
  ...day.existing.map((meal) => ({ meal, isPreview: false as const })),
]

const toggleDayPopover = (day: CalendarDay, event: MouseEvent) => {
  if (openDayKey.value === day.utcKey) {
    openDayKey.value = null
    return
  }
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  popoverStyle.value = { top: `${rect.bottom + 4}px`, left: `${rect.left}px` }
  openDayKey.value = day.utcKey
}

const closeDayPopover = () => {
  openDayKey.value = null
}

// Close on Escape, and on any click that's outside both the popover and an
// overflow-badge trigger (a trigger click is left to its own toggle handler).
const onDocKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') closeDayPopover()
}
const onDocMouseDown = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (target.closest('.cal-overflow')) return
  if (openDayKey.value && popoverRef.value && !popoverRef.value.contains(target)) closeDayPopover()
}
onMounted(() => {
  document.addEventListener('keydown', onDocKeydown)
  document.addEventListener('mousedown', onDocMouseDown)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onDocKeydown)
  document.removeEventListener('mousedown', onDocMouseDown)
})

const isSelectedDay = (day: CalendarDay) => !!props.selectedDate && day.utcKey === props.selectedDate

const selectDay = (day: CalendarDay) => {
  closeDayPopover()
  if (!day.isCurrentMonth) return
  emit('select-date', day.utcKey)
}
</script>

<template>
  <div class="cal-card card-elevated overflow-hidden">
    <!-- Month navigation -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
      <button @click="prevMonth" class="cal-nav-btn" aria-label="Previous month">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <div class="flex items-center gap-3">
        <h3 class="text-base font-bold text-gray-900">{{ currentMonth.format('MMMM YYYY') }}</h3>
        <button v-if="!isCurrentMonthNow" @click="goToToday" class="cal-today-btn">Today</button>
      </div>

      <button @click="nextMonth" class="cal-nav-btn" aria-label="Next month">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Legend -->
    <div class="cal-legend flex-shrink-0">
      <span class="cal-legend-label">Key:</span>
      <span class="cal-legend-item cal-legend-item--preview">
        <svg class="cal-legend-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        Reused meal
      </span>
      <span class="cal-legend-item cal-legend-item--existing">Existing meal</span>
    </div>

    <!-- Day-of-week headers -->
    <div class="cal-dow-row flex-shrink-0">
      <div v-for="dow in WEEK_DAYS" :key="dow" class="cal-dow-cell">{{ dow }}</div>
    </div>

    <!-- Grid -->
    <div class="cal-grid-area">
      <div class="cal-grid">
        <div
          v-for="day in calendarDays"
          :key="day.utcKey"
          class="cal-cell"
          :class="{
            'cal-cell--other-month': !day.isCurrentMonth,
            'cal-cell--active':      day.isCurrentMonth,
            'cal-cell--has-meals':   day.isCurrentMonth && !day.isToday && (day.existing.length > 0 || day.preview.length > 0),
            'cal-cell--has-preview': day.isCurrentMonth && day.preview.length > 0,
            'cal-cell--today':       day.isToday,
            'cal-cell--selected':    isSelectedDay(day),
          }"
          :role="day.isCurrentMonth ? 'button' : undefined"
          :tabindex="day.isCurrentMonth ? 0 : undefined"
          :aria-pressed="day.isCurrentMonth ? isSelectedDay(day) : undefined"
          :aria-label="day.isCurrentMonth ? `Set target date to ${day.date.format('D MMMM YYYY')}` : undefined"
          @click="selectDay(day)"
          @keydown.enter="selectDay(day)"
          @keydown.space.prevent="selectDay(day)"
        >
          <span class="cal-day-num">{{ day.date.date() }}</span>
          <span v-if="isSelectedDay(day)" class="cal-target-tag">Target</span>

          <div
            v-if="day.existing.length > 0 || day.preview.length > 0"
            class="cal-meals"
          >
            <div
              v-for="{ meal, isPreview } in visiblePills(day)"
              :key="`${isPreview ? 'preview' : 'existing'}-${meal.id}`"
              class="cal-meal-pill"
              :class="{ 'cal-meal-pill--preview': isPreview }"
            >
              <span v-if="isPreview" class="cal-preview-tag">Reused</span>
              <span class="cal-meal-text">
                {{ meal.title }}
                <span v-if="getMealTypeName(meal)" class="cal-meal-type">· {{ getMealTypeName(meal) }}</span>
              </span>
              <span
                v-if="(meal.portions_days ?? 1) > 1"
                class="cal-multiday-badge"
                :class="meal.portions_mode === 'repeat' ? 'cal-multiday-badge--repeat' : 'cal-multiday-badge--leftovers'"
              >
                <svg class="cal-multiday-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    v-if="meal.portions_mode === 'repeat'"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                  <template v-else>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2.5"
                      d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2.5"
                      d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z"
                    />
                  </template>
                </svg>{{ meal.portions_days }}d
              </span>
            </div>
            <button
              v-if="overflowCount(day) > 0"
              type="button"
              class="cal-overflow"
              @click.stop="toggleDayPopover(day, $event)"
              :aria-expanded="openDayKey === day.utcKey"
              :aria-label="`Show all meals on ${day.date.format('D MMMM YYYY')}`"
            >
              +{{ overflowCount(day) }} More
            </button>
          </div>

          <!-- Persistent (not hover-only) cue that current-month cells are clickable to set the target date. -->
          <svg v-if="day.isCurrentMonth" class="cal-click-hint" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="7" stroke-width="2" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Day-detail popover — the escape hatch for days with more than the visible pill cap. -->
    <Teleport to="body">
      <div
        v-if="openDayKey"
        ref="popoverRef"
        class="cal-day-popover"
        :style="popoverStyle"
        role="dialog"
        :aria-label="`Meals on ${openDayLabel}`"
      >
        <div class="cal-day-popover-header">
          <span>{{ openDayLabel }}</span>
          <button type="button" class="cal-day-popover-close" @click="closeDayPopover" aria-label="Close">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div
          v-for="{ meal, isPreview } in openDayMeals"
          :key="`${isPreview ? 'preview' : 'existing'}-${meal.id}`"
          class="cal-day-popover-row"
          :class="{ 'cal-day-popover-row--preview': isPreview }"
        >
          <span v-if="isPreview" class="cal-day-popover-tag">Reused</span>
          <span class="cal-day-popover-title">{{ meal.title }}</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* ── Card ────────────────────────────────────────────────────────────────── */
.cal-card {
  display: flex;
  flex-direction: column;
  height: 520px;
}

/* ── Day-of-week header row ─────────────────────────────────────────────── */
.cal-dow-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
}
.cal-dow-cell {
  text-align: center;
  padding: 8px 0;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.04em;
}

/* ── Grid area ───────────────────────────────────────────────────────────── */
.cal-grid-area {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.cal-grid {
  display: grid;
  height: 100%;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 1fr;
  gap: 4px;
  padding: 4px;
  background-color: #f6f9f6;
}

/* ── Cell base ──────────────────────────────────────────────────────────── */
.cal-cell {
  position: relative;
  border-radius: 6px;
  padding: 7px;
  background-color: white;
  overflow: hidden;
}
.cal-cell--other-month {
  background-color: #fafafa;
  opacity: 0.5;
}
.cal-cell--has-meals {
  background-color: #eaf1eb;
}
.cal-cell--has-preview {
  outline: 2px solid #4f8646;
  outline-offset: -2px;
}
.cal-cell--today {
  background-color: #4f8646e5;
}
.cal-cell--active {
  cursor: pointer;
}
.cal-cell--active:hover,
.cal-cell--active:focus-visible {
  filter: brightness(0.96);
}
.cal-cell--active:focus-visible {
  outline: 2px solid #4f8646;
  outline-offset: 1px;
}
.cal-cell--selected {
  outline: 3px solid #4f8646;
  outline-offset: -3px;
}

/* ── Day number ─────────────────────────────────────────────────────────── */
.cal-day-num {
  display: block;
  text-align: center;
  font-size: 13px;
  font-weight: 400;
  color: #0f172a;
  margin-bottom: 3px;
  line-height: 1.3;
}
.cal-cell--other-month .cal-day-num {
  color: #cbd5e1;
}
.cal-cell--today .cal-day-num {
  color: white;
  font-weight: 600;
}

.cal-target-tag {
  display: block;
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #4f8646;
  margin: -1px 0 3px;
}
.cal-cell--today .cal-target-tag {
  color: white;
}

/* ── Persistent click-to-select-date affordance ─────────────────────────── */
.cal-click-hint {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 12px;
  height: 12px;
  color: #4f8646;
  opacity: 0.35;
  pointer-events: none;
  transition: opacity 150ms ease;
}
.cal-cell--active:hover .cal-click-hint,
.cal-cell--active:focus-visible .cal-click-hint {
  opacity: 0.8;
}
.cal-cell--today .cal-click-hint {
  color: white;
  opacity: 0.55;
}
.cal-cell--selected .cal-click-hint {
  opacity: 0.8;
}

/* ── Meal pills ─────────────────────────────────────────────────────────── */
.cal-meals {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.cal-meal-pill {
  display: block;
  background-color: white;
  border-left: 3px solid transparent;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}
.cal-meal-pill--preview {
  border-left-color: #4f8646;
  background-color: #eaf1eb;
  color: #2d5a26;
}
.cal-preview-tag {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #4f8646;
}
.cal-meal-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.cal-meal-type {
  font-weight: 500;
  opacity: 0.7;
  font-size: 0.9em;
}

.cal-multiday-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  font-size: 9px;
  font-weight: 700;
  border-radius: 3px;
  padding: 0 3px;
  vertical-align: middle;
  line-height: 14px;
}
.cal-multiday-badge--leftovers {
  background: var(--color-primary-50, #edf3ec);
  color: var(--color-primary-700, #3f4a3c);
}
.cal-multiday-badge--repeat {
  background: #eff6ff;
  color: #1d4ed8;
}
.cal-multiday-icon {
  width: 9px;
  height: 9px;
  flex-shrink: 0;
}

/* ── Legend strip ───────────────────────────────────────────────────────── */
.cal-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 4px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
}
.cal-legend-label {
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 0.04em;
  margin-right: 2px;
}
.cal-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
}
.cal-legend-item--preview {
  background: #eaf1eb;
  color: #2d5a26;
}
.cal-legend-item--existing {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}
.cal-legend-icon {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
}

/* ── Overflow count (opens the day-detail popover) ──────────────────────── */
.cal-overflow {
  display: block;
  font-family: inherit;
  border: none;
  background: none;
  font-size: 10px;
  font-weight: 700;
  color: #3f4a3c;
  padding: 1px 4px;
  text-decoration: underline dotted;
  cursor: pointer;
}
.cal-overflow:hover,
.cal-overflow:focus-visible {
  text-decoration-style: solid;
}
.cal-cell--today .cal-overflow {
  color: white;
}

/* ── Day-detail popover ──────────────────────────────────────────────────── */
.cal-day-popover {
  position: fixed;
  z-index: 60;
  width: 240px;
  max-height: 280px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
}
.cal-day-popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
  margin-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
}
.cal-day-popover-close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: #64748b;
  cursor: pointer;
}
.cal-day-popover-close:hover {
  background: #f1f5f9;
  color: #0f172a;
}
.cal-day-popover-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 4px;
  border-left: 3px solid transparent;
  border-radius: 4px;
  font-size: 13px;
  color: #0f172a;
}
.cal-day-popover-row--preview {
  border-left-color: #4f8646;
  background-color: rgba(79, 134, 70, 0.06);
}
.cal-day-popover-tag {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #4f8646;
}
.cal-day-popover-title {
  min-width: 0;
  word-break: break-word;
}

/* ── Navigation buttons ─────────────────────────────────────────────────── */
.cal-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  transition: background-color 150ms ease, color 150ms ease;
}
.cal-nav-btn:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

/* ── Today shortcut button ──────────────────────────────────────────────── */
.cal-today-btn {
  font-size: 12px;
  font-weight: 600;
  color: #4f8646;
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  transition: background-color 150ms ease;
}
.cal-today-btn:hover {
  background-color: #eaf1eb;
}
</style>
